import { useCallback, useEffect, useState } from 'react';
import type { Role } from './rolesData';

type RolesUpdater = Role[] | ((prev: Role[]) => Role[]);

const DEFAULT_WORKSPACE_KEY = '__default__';
const sharedRolesByWorkspaceId = new Map<string, Role[]>();
const listenersByWorkspaceId = new Map<string, Set<(roles: Role[]) => void>>();

const getWorkspaceKey = (workspaceId?: string | null) => workspaceId || DEFAULT_WORKSPACE_KEY;

const getSharedRolesState = (workspaceKey: string) => sharedRolesByWorkspaceId.get(workspaceKey) || [];

const getListeners = (workspaceKey: string) => {
    let listeners = listenersByWorkspaceId.get(workspaceKey);
    if (!listeners) {
        listeners = new Set<(roles: Role[]) => void>();
        listenersByWorkspaceId.set(workspaceKey, listeners);
    }

    return listeners;
};

const updateSharedRolesState = (workspaceKey: string, updater: RolesUpdater) => {
    const previousRoles = getSharedRolesState(workspaceKey);
    const nextRoles = typeof updater === 'function'
        ? (updater as (prev: Role[]) => Role[])(previousRoles)
        : updater;
    sharedRolesByWorkspaceId.set(workspaceKey, nextRoles);

    getListeners(workspaceKey).forEach(listener => listener(nextRoles));
};

export const useSharedRoles = (workspaceId?: string | null) => {
    const workspaceKey = getWorkspaceKey(workspaceId);
    const [roles, setRoles] = useState<Role[]>(getSharedRolesState(workspaceKey));
    const updateRoles = useCallback(
        (updater: RolesUpdater) => updateSharedRolesState(workspaceKey, updater),
        [workspaceKey]
    );

    useEffect(() => {
        setRoles(getSharedRolesState(workspaceKey));
        const listeners = getListeners(workspaceKey);
        listeners.add(setRoles);

        return () => {
            listeners.delete(setRoles);
        };
    }, [workspaceKey]);

    return [roles, updateRoles] as const;
};
